(() => {
  /* global Swiper */
  const COMPONENT_NAME = "product-card";
  const INIT_ROOT_MARGIN = "200px";
  const SWIPER_RETRY_LIMIT = 40;
  const SWIPER_RETRY_DELAY = 50;
  const NUDGE_DELAY = 600;
  const NUDGE_PEEK_RATIO = 0.3;
  const NUDGE_SPEED = 400;
  const NUDGE_HOLD = 500;
  const NUDGE_SESSION_KEY = "roka:product-card-nudge";
  const NUDGE_VISIBLE_RATIO = 0.5;
  /* The stylesheet scrolls the slides natively under the complement of this
     query, so the two have to stay in step. */
  const FINE_POINTER_QUERY = "(hover: hover) and (pointer: fine)";
  const SLIDE_VISIBLE_RATIO = 0.6;
  const VIDEO_SELECTOR = ".product-card__slide video";
  const RATING_ATTR = "data-ls-product-rating";
  const VOTES_ATTR = "data-ls-product-votes";
  /* Lipscore scores are 0-10; the design shows them out of 5. */
  const RATING_SCALE = 2;

  /* A touch on a card fires pointerover too. The stylesheet only shows the
     hover image under FINE_POINTER_QUERY, and the image has no lazy loading
     to hold it back, so a touch screen would download it for nothing. */
  const hydrateHoverImage = (event) => {
    if (!window.matchMedia(FINE_POINTER_QUERY).matches) return;

    const target = event.target;

    if (!(target instanceof Element)) return;

    const card = target.closest("product-card");

    if (!card) return;

    const image = card.querySelector(".product-card__image--hover[data-src]");

    if (!image) return;

    const src = image.getAttribute("data-src");

    if (!src) return;

    const srcset = image.getAttribute("data-srcset");
    const sizes = image.getAttribute("data-sizes");

    if (srcset) image.setAttribute("srcset", srcset);
    if (sizes) image.setAttribute("sizes", sizes);

    image.setAttribute("src", src);
    image.removeAttribute("data-src");
  };

  if (!window.__rokaProductCardHover) {
    window.__rokaProductCardHover = true;
    document.addEventListener("pointerover", hydrateHoverImage, true);
    document.addEventListener("focusin", hydrateHoverImage);
  }

  if (customElements.get(COMPONENT_NAME)) return;

  class ProductCard extends HTMLElement {
    #media = null;
    #swiperElement = null;
    #swiper = null;
    #slideCount = 0;
    #hasVideo = false;
    #observer = null;
    #timers = [];
    #reviews = null;
    #reviewText = null;
    #reviewCount = null;
    #reviewSource = null;
    #reviewObserver = null;
    #nudgeObserver = null;
    #nudgeVisible = false;
    #scroller = null;
    #slideObserver = null;
    #activeSlide = null;
    #pointerQuery = null;

    connectedCallback() {
      this.#initReviews();

      this.#media = this.querySelector("[data-product-card-media]");
      this.#swiperElement = this.querySelector("[data-product-card-swiper]");
      this.#slideCount = this.querySelectorAll(
        "[data-product-card-slide]",
      ).length;
      this.#hasVideo = this.querySelector(VIDEO_SELECTOR) !== null;

      if (!this.#media || !this.#swiperElement) return;
      if (this.#slideCount < 2) return;

      const pointerQuery = window.matchMedia(FINE_POINTER_QUERY);

      if (!pointerQuery.matches) {
        this.#pointerQuery = pointerQuery;
        this.#pointerQuery.addEventListener(
          "change",
          this.#handlePointerChange,
        );
        this.#initNativeSlides();
        return;
      }

      this.#observeForInit();
    }

    /* Paint our own rating pill from whatever Lipscore's widget resolves.
       Their script writes the score back onto the mounted span as data
       attributes once it has hydrated, so watch for those rather than
       restyling their markup. Silent no-op if Lipscore never loads. */
    #initReviews() {
      this.#reviews = this.querySelector("[data-product-card-reviews]");
      this.#reviewText = this.querySelector("[data-product-card-review-text]");
      this.#reviewCount = this.querySelector(
        "[data-product-card-review-count]",
      );
      this.#reviewSource = this.querySelector("[data-lipscore-tile]");

      if (!this.#reviews || !this.#reviewText) return;
      if (!this.#reviewSource) return;
      if (this.#syncReviews()) return;

      this.#reviewObserver = new MutationObserver(() => this.#syncReviews());
      this.#reviewObserver.observe(this.#reviewSource, {
        attributes: true,
        attributeFilter: [RATING_ATTR, VOTES_ATTR],
      });

      /* The widget is inert until the Lipscore loader promotes it near the
         screen (assets/lipscore-loader.js). Cards in the first render are
         collected by the loader when it runs; later ones (load more,
         filters, drawers) register here. */
      window.RokaLipscore?.watch(this.#reviewSource);
    }

    #syncReviews() {
      const rating = Number(this.#reviewSource.getAttribute(RATING_ATTR));
      const votes = Number(this.#reviewSource.getAttribute(VOTES_ATTR));

      if (!Number.isFinite(rating) || rating <= 0) return false;
      if (!Number.isFinite(votes) || votes <= 0) return false;

      this.#reviewText.textContent = (rating / RATING_SCALE).toFixed(1);

      if (this.#reviewCount) {
        const countTemplate =
          this.#reviews.dataset.reviewCountTemplate?.trim() || "(%count%)";

        this.#reviewCount.textContent = countTemplate.replace(
          "%count%",
          String(votes),
        );
      }
      this.#reviews.hidden = false;

      this.#reviewObserver?.disconnect();
      this.#reviewObserver = null;

      return true;
    }

    disconnectedCallback() {
      window.RokaLipscore?.unwatch(this.#reviewSource);
      this.#reviewObserver?.disconnect();
      this.#reviewObserver = null;
      this.#stopWatchingPointer();
      this.#stopSlides();
      this.#swiper?.destroy(true, true);
      this.#swiper = null;
    }

    /* The observers, timers and videos of either kind of strip. */
    #stopSlides() {
      this.#observer?.disconnect();
      this.#observer = null;
      this.#nudgeObserver?.disconnect();
      this.#nudgeObserver = null;
      this.#slideObserver?.disconnect();
      this.#slideObserver = null;

      for (const timer of this.#timers) {
        window.clearTimeout(timer);
      }

      this.#timers = [];

      /* Teardown can land mid-peek, with its restore timer just cleared. */
      this.#scroller?.style.removeProperty("scroll-snap-type");
      this.#scroller = null;
      this.#activeSlide = null;

      this.#pauseVideos();
    }

    #stopWatchingPointer() {
      this.#pointerQuery?.removeEventListener(
        "change",
        this.#handlePointerChange,
      );
      this.#pointerQuery = null;
    }

    /* A touch screen that gains a mouse or trackpad (a tablet docked, a
       convertible folded back) loses the native strip in the stylesheet, so
       the card moves to Swiper and its arrows. */
    #handlePointerChange = (event) => {
      if (!event.matches) return;

      this.#stopWatchingPointer();
      this.#stopSlides();
      this.#observeForInit();
    };

    /* Touch screens scroll the slides natively (see the stylesheet). Swiper
       did two jobs besides the swipe, and they move here: play the video on
       the slide the shopper lands on, and the once-a-session peek. */
    #initNativeSlides() {
      this.#scroller = this.#swiperElement.querySelector(".swiper-wrapper");

      if (!this.#scroller) return;

      if (this.#hasVideo && "IntersectionObserver" in window) {
        this.#watchNativeVideos();
      }

      this.#maybeNudge();
    }

    /* Mirrors the Swiper path: nothing plays until the shopper swipes to a
       video, and leaving the screen pauses it. The opening slide counts as
       landed already, so a card that opens on a video still waits for a
       swipe, as it did under Swiper. */
    #watchNativeVideos() {
      this.#activeSlide = this.#scroller.firstElementChild;

      this.#slideObserver = new IntersectionObserver(
        (entries) => {
          const landed = entries.find((entry) => entry.isIntersecting)?.target;

          if (!landed || landed === this.#activeSlide) return;

          this.#activeSlide = landed;
          this.#playVideoIn(landed);
        },
        { root: this.#scroller, threshold: SLIDE_VISIBLE_RATIO },
      );

      for (const slide of this.#scroller.children) {
        this.#slideObserver.observe(slide);
      }

      this.#observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) {
            this.#pauseVideos();
          }
        },
        { rootMargin: INIT_ROOT_MARGIN },
      );

      this.#observer.observe(this);
    }

    /* Only pay for a Swiper instance once the card is near the viewport.
         Cards with video keep the observer after init: a muted loop goes on
         decoding off-screen, so scrolling past one has to stop it. */
    #observeForInit() {
      if (!("IntersectionObserver" in window)) {
        this.#initSwiper();
        return;
      }

      this.#observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) {
            this.#pauseVideos();
            return;
          }

          if (!this.#hasVideo) {
            this.#observer.disconnect();
            this.#observer = null;
          }

          this.#initSwiper();
        },
        { rootMargin: INIT_ROOT_MARGIN },
      );

      this.#observer.observe(this);
    }

    #pauseVideos(except = null) {
      for (const video of this.querySelectorAll(VIDEO_SELECTOR)) {
        if (video === except) continue;

        video.pause();
      }
    }

    /* Play the video the shopper landed on, and only that one. Nothing plays
         on init, so a card that is merely scrolled past never spends the
         bytes — with preload="none" the play() call is what starts the
         download. play() rejects when the browser blocks it; swallow it. */
    #playActiveVideo() {
      if (!this.#swiper) return;

      this.#playVideoIn(this.#swiper.slides?.[this.#swiper.activeIndex]);
    }

    #playVideoIn(slide) {
      const video = slide?.querySelector("video") || null;

      this.#pauseVideos(video);

      if (!video) return;
      if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
        return;
      }

      video.play()?.catch(() => {});
    }

    #initSwiper(retryCount = 0) {
      if (this.#swiper) return;

      if (typeof Swiper === "undefined") {
        if (retryCount >= SWIPER_RETRY_LIMIT) return;

        if (document.readyState === "complete") {
          this.#timers.push(
            window.setTimeout(
              () => this.#initSwiper(retryCount + 1),
              SWIPER_RETRY_DELAY,
            ),
          );
        } else {
          window.addEventListener(
            "load",
            () => this.#initSwiper(retryCount + 1),
            { once: true },
          );
        }

        return;
      }

      const allowTouchMove = this.dataset.disableMediaTouch !== "true";
      const prevEl = this.querySelector("[data-product-card-prev]");
      const nextEl = this.querySelector("[data-product-card-next]");

      this.#swiper = new Swiper(this.#swiperElement, {
        slidesPerView: 1,
        loop: true,
        allowTouchMove,
        /* Desktop is arrows-only per design; drag stays touch-native. */
        simulateTouch: false,
        watchOverflow: true,
        navigation: {
          prevEl,
          nextEl,
          disabledClass: "product-card__nav-button--disabled",
        },
        /* Swiper's a11y module rewrites the button labels on init, so feed it
             the translated strings or its English defaults ship to production. */
        a11y: {
          prevSlideMessage: this.dataset.prevLabel || undefined,
          nextSlideMessage: this.dataset.nextLabel || undefined,
        },
        /* Pause on slideChange so the outgoing clip stops the moment the
             swipe commits; play on transition end so the video only starts
             once its slide has actually settled. */
        on: {
          slideChange: () => this.#pauseVideos(),
          slideChangeTransitionEnd: () => this.#playActiveVideo(),
        },
      });
    }

    /* sessionStorage throws when cookies are blocked or storage is full.
         Treat any failure as "already nudged" so the animation can never run
         on a loop for someone whose browser will not remember it. */
    #nudgeAlreadySeen() {
      try {
        return window.sessionStorage.getItem(NUDGE_SESSION_KEY) === "1";
      } catch (error) {
        return true;
      }
    }

    #markNudgeSeen() {
      try {
        window.sessionStorage.setItem(NUDGE_SESSION_KEY, "1");
      } catch (error) {
        /* Nothing to do — the guard above already fails closed. */
      }
    }

    /* Peek at slide 2 on touch devices to advertise the carousel. Once per
       session, per the ticket: it is a hint, not a decoration. Touch
       devices are the native strip, so that is the only path it runs on. */
    #maybeNudge() {
      if (!this.#scroller) return;
      if (this.dataset.disableMediaTouch === "true") return;
      if (this.#nudgeAlreadySeen()) return;
      if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
        return;
      }

      const item = this.closest(".collection-page__item");

      if (!item) return;
      if (item.parentElement?.firstElementChild !== item) return;

      /* The card can still be below the fold here. Wait for the media to be
         genuinely on screen before starting the delay — otherwise the one
         nudge the session gets is spent on an animation nobody saw. */
      if (!("IntersectionObserver" in window)) {
        this.#nudgeVisible = true;
        this.#scheduleNudge();
        return;
      }

      this.#nudgeObserver = new IntersectionObserver(
        (entries) => {
          const visible = entries.some((entry) => entry.isIntersecting);

          if (visible === this.#nudgeVisible) return;

          this.#nudgeVisible = visible;

          if (visible) this.#scheduleNudge();
        },
        { threshold: NUDGE_VISIBLE_RATIO },
      );

      this.#nudgeObserver.observe(this.#media);
    }

    #scheduleNudge() {
      this.#timers.push(
        window.setTimeout(() => {
          const scroller = this.#scroller;

          if (!scroller) return;
          /* Already swiped — the hint has done its job. */
          if (scroller.scrollLeft > 0) return;
          /* Scrolled back off during the delay — leave it for the next time
               the card comes into view. */
          if (!this.#nudgeVisible) return;

          /* Claim the session here, not when the timer is scheduled — a card
               that scrolls out and gets torn down first never animated, so it
               should not spend the one nudge the session gets. */
          if (this.#nudgeAlreadySeen()) return;
          this.#markNudgeSeen();

          this.#nudgeObserver?.disconnect();
          this.#nudgeObserver = null;

          const peek = scroller.clientWidth * NUDGE_PEEK_RATIO;

          /* Mandatory snapping pulls a programmatic scroll straight back to
             the nearest slide, so it is lifted for the peek and restored
             once the strip is home. A touch during the peek ends it there
             and then, so the scroll home never undoes the shopper's swipe. */
          let returnTimer = 0;
          let restoreTimer = 0;
          const touching = new AbortController();
          const endPeek = () => {
            window.clearTimeout(returnTimer);
            window.clearTimeout(restoreTimer);
            touching.abort();
            scroller.style.removeProperty("scroll-snap-type");
          };

          scroller.addEventListener("pointerdown", endPeek, {
            passive: true,
            signal: touching.signal,
          });
          scroller.addEventListener("touchstart", endPeek, {
            passive: true,
            signal: touching.signal,
          });

          scroller.style.scrollSnapType = "none";
          scroller.scrollTo({ left: peek, behavior: "smooth" });

          returnTimer = window.setTimeout(() => {
            scroller.scrollTo({ left: 0, behavior: "smooth" });
            restoreTimer = window.setTimeout(endPeek, NUDGE_SPEED + NUDGE_HOLD);
            this.#timers.push(restoreTimer);
          }, NUDGE_SPEED + NUDGE_HOLD);
          this.#timers.push(returnTimer);
        }, NUDGE_DELAY),
      );
    }
  }

  customElements.define(COMPONENT_NAME, ProductCard);
})();
